import { Author, Hero, Maybe, Seo } from '@/lib/types';

import AuthorShortBio from '@/components/author-short-bio';
import DatePublishedModified from '@/components/date-published-modified';
import DefaultLayout from '@/components/layout/default-layout';
import Row from '@/components/row';
import Section from '@/components/section';
import Separator from '@/components/separator';
import TemplateBreadcrumb, {
  TemplateBreadcrumbItem,
} from '@/components/template/template-breadcrumb';
import {
  ContentComponentType,
  SidebarComponentType,
} from '@/components/template/template-types';
import Typography from '@/components/typography';

import { RowLayout } from '@/enums/row-layout';
import { Spacing } from '@/enums/spacing';
import { TemplateLayout } from '@/enums/template-layout';
import { getRenderer, getRendererData } from '@/hygraph/renderers';
import HeroRenderer from '@/hygraph/renderers/hero.renderer';

import LinkedSection from './linked-section';
import TemplateHeader from './template-header';
import TemplateTableOfContents, {
  TableOfContentsItem,
} from './template-table-of-contents';

type Nullable<T> = T | null;
export type TemplateDetailRendererProps = {
  hero?: Nullable<Hero>;
  breadcrumbs?: TemplateBreadcrumbItem[];
  title?: Nullable<string>;
  author?: Nullable<Author>;
  tocEnabled?: Nullable<boolean>;
  tableOfContents?: TableOfContentsItem[];
  layout?: TemplateLayout;
  // now this could be anything
  // if we do it like this, we'll have to
  // update it for any new tempalte
  components?: ContentComponentType;
  sidebarComponents?: SidebarComponentType;
  publishedAt: string;
  modifiedAt: string;
  preview?: boolean;
  estimatedReadingTime?: Nullable<number>;
  seo: Maybe<Seo> | null | undefined;
  tocItems?: Nullable<TableOfContentsItem[]>;
};

const rowLayoutTemplateLayoutMap = {
  [TemplateLayout.Single_Column]: RowLayout.MAX_WIDTH_CENTER,
  [TemplateLayout.With_Sidebar]: RowLayout.ONE_COLUMN_WITH_SIDEBAR,
};

const TemplateRenderer = (props: TemplateDetailRendererProps) => {
  if (!props.layout) {
    props.layout = TemplateLayout.With_Sidebar;
  }

  return (
    <DefaultLayout preview={props.preview}>
      <main>
        <Section>
          {props.breadcrumbs && (
            <Row layout={RowLayout.FULL_WIDE}>
              <TemplateBreadcrumb breadcrumbs={props.breadcrumbs} />
            </Row>
          )}
          <Row layout={RowLayout.FULL_WIDE} className='pb-8'>
            <Typography.Heading1 className='mb-4'>
              {props.title}
            </Typography.Heading1>
            {props.author && (
              <TemplateHeader
                author={props.author}
                updatedAt={props.modifiedAt}
                minutes={props.estimatedReadingTime}
              />
            )}
          </Row>

          {props.hero && (
            <>
              <HeroRenderer hero={props.hero} />
              <Separator spacing={Spacing.MEDIUM} />
            </>
          )}

          <Row layout={rowLayoutTemplateLayoutMap[props.layout]}>
            <div>
              {props.tocItems && props.tocItems?.length > 0 && (
                <TemplateTableOfContents links={props.tocItems} />
              )}

              <div>
                {props.components?.map((component, index) => {
                  const Renderer = getRenderer(component.__typename);
                  if (!Renderer) return null;

                  const props = getRendererData({ ...component, index });

                  return (
                    <LinkedSection key={component.id} component={component}>
                      <Renderer {...props} />
                    </LinkedSection>
                  );
                })}
              </div>
            </div>

            {props.layout === TemplateLayout.With_Sidebar &&
              props.sidebarComponents && (
                <div className='flex flex-col gap-8'>
                  {props.sidebarComponents.map((component, index) => {
                    const Renderer = getRenderer(component.__typename);
                    if (!Renderer) return null;
                    const props = getRendererData({ ...component, index });
                    return <Renderer key={component.id} {...props} />;
                  })}
                </div>
              )}
          </Row>
          <div />
        </Section>
        {props.author && (
          <Section>
            <div className='px-3 py-8'>
              <AuthorShortBio {...props.author} />

              <DatePublishedModified
                datePublished={props.publishedAt}
                dateModified={props.modifiedAt}
                className='layout mx-auto mt-4'
              />
            </div>
          </Section>
        )}
        <Separator spacing={Spacing.MEDIUM} />
      </main>
    </DefaultLayout>
  );
};

export default TemplateRenderer;
